import React, { useState, useEffect } from 'react';
import { 
  message, Table, Input, DatePicker, Space, Button, Modal, Typography, 
  Card, Timeline, Tag, Tooltip, Select, Statistic, Row, Col 
} from 'antd';
import { 
  SearchOutlined, CalendarOutlined, TeamOutlined, 
  MessageOutlined, SyncOutlined, UserOutlined, RobotOutlined 
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { adminInstance } from './api/axiosConfig';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const ChatLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [statistics, setStatistics] = useState({
    totalSessions: 0,
    activeToday: 0,
    averageMessagesPerChat: 0,
    totalMessages: 0
  });

  useEffect(() => {
    fetchWorkflows();
    fetchLogs();
    fetchStatistics();
  }, [pagination.current, pagination.pageSize, dateRange, searchText, selectedWorkflow]);

  const fetchWorkflows = async () => {
    try {
      const response = await adminInstance.get('/workflows');
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await adminInstance.get('/chat-statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching chat statistics:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.current,
        per_page: pagination.pageSize,
        search: searchText,
        workflow_id: selectedWorkflow,
        start_date: dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date: dateRange?.[1]?.format('YYYY-MM-DD')
      };

      const response = await adminInstance.get('/chat-logs', { params });
      
      setLogs(response.data.logs);
      setPagination(prev => ({
        ...prev,
        total: response.data.total
      }));
    } catch (error) {
      console.error('Error fetching chat logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp) => {
    return dayjs.utc(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };
  
  const getRelativeTime = (timestamp) => {
    return dayjs.utc(timestamp).fromNow();
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleWorkflowChange = (value) => {
    setSelectedWorkflow(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleViewDetails = async (record) => {
    try {
      const response = await adminInstance.get(`/chat-messages/${record.session_id}`);
      setSelectedChat({
        ...record,
        messages: response.data
      });
      setDetailsVisible(true);
    } catch (error) {
      console.error('Error fetching chat details:', error);
    }
  };

  const columns = [
    {
      title: 'Workflow',
      dataIndex: 'workflow_name',
      key: 'workflow_name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Session ID',
      dataIndex: 'session_id',
      key: 'session_id',
      render: (text) => <Text code>{text.slice(0, 8)}...</Text>
    },
    {
      title: 'Messages',
      dataIndex: 'message_count',
      key: 'message_count',
      sorter: true,
      render: (count) => (
        <Tag color="blue">
          <MessageOutlined /> {count}
        </Tag>
      )
    },
    {
      title: 'First Interaction',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      render: (date) => (
        <Space direction="vertical" size={0}>
          <span>{formatDateTime(date)}</span>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            UTC
          </Text>
        </Space>
      )
    },
    {
      title: 'Last Activity',
      dataIndex: 'updated_at',
      key: 'updated_at',
      sorter: true,
      render: (date) => (
        <Tooltip title={formatDateTime(date) + ' UTC'}>
        <Space direction="vertical" size={0}>
          <span>{getRelativeTime(date)}</span>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs.utc(date).format('HH:mm')} UTC
          </Text>
        </Space>
      </Tooltip>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary" 
          onClick={() => handleViewDetails(record)}
          icon={<MessageOutlined />}
        >
          View Chat
        </Button>
      )
    }
  ];

  const renderChatDetails = () => {
    if (!selectedChat) return null;

    return (
      <Modal
        title={
          <Space>
            <MessageOutlined />
            <span>Chat Session Details</span>
            <Tag color="blue">{selectedChat.workflow_name}</Tag>
          </Space>
        }
        visible={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={800}
        footer={null}
      >
        <Card>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic 
                title="Total Messages" 
                value={selectedChat.message_count} 
                prefix={<MessageOutlined />} 
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="Session Duration"
                value={dayjs.utc(selectedChat.updated_at).diff(dayjs.utc(selectedChat.created_at), 'minute')}
                suffix="minutes"
              />
            </Col>
          </Row>
        </Card>

        <Timeline style={{ marginTop: 24 }}>
          {selectedChat.messages?.map((message, index) => (
            <Timeline.Item
              key={index}
              dot={message.role === 'user' ? <UserOutlined /> : <RobotOutlined />}
              color={message.role === 'user' ? 'blue' : 'green'}
            >
              <Card
                size="small"
                style={{ 
                  backgroundColor: message.role === 'user' ? '#f0f5ff' : '#f6ffed',
                  marginBottom: 16
                }}
              >
                <Space direction="vertical">
                  <Text strong>
                    {message.role === 'user' ? 'User' : 'Assistant'}
                  </Text>
                  <Text>{message.content}</Text>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {dayjs(message.created_at).format('YYYY-MM-DD HH:mm:ss')}
                  </Text>
                </Space>
              </Card>
            </Timeline.Item>
          ))}
        </Timeline>
      </Modal>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Chat Logs</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Chat Sessions"
              value={statistics.totalSessions}
              prefix={<TeamOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Active Today"
              value={statistics.activeToday}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Average Messages/Chat"
              value={statistics.averageMessagesPerChat}
              precision={1}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Messages"
              value={statistics.totalMessages}
              prefix={<MessageOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by session ID or content"
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <RangePicker 
          onChange={handleDateRangeChange}
          style={{ width: 300 }}
        />
        <Select
          placeholder="Select Workflow"
          style={{ width: 200 }}
          onChange={handleWorkflowChange}
          allowClear
        >
          {workflows.map(workflow => (
            <Option key={workflow.id} value={workflow.id}>
              {workflow.name}
            </Option>
          ))}
        </Select>
        <Button 
          icon={<SyncOutlined />} 
          onClick={() => fetchLogs()}
        >
          Refresh
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={logs}
        loading={loading}
        pagination={pagination}
        onChange={(newPagination) => setPagination(newPagination)}
        rowKey="session_id"
      />

      {renderChatDetails()}
    </div>
  );
};

export default ChatLogs;
